/**
 * 错误现场快照（P0-18/19）
 *
 * 面包屑答「做了什么」，快照答「当时是什么状态」。低基数字段进 extra（可在异常
 * 看板直接筛选/分组），高基数/长文本另发一条 sendLog 承载，不污染维度。
 *
 * 非 JS 错误场景（画布保存失败、SSE 断流等）不会被 SDK 自动附带面包屑 ——
 * 此时需业务侧主动调用 getBreadcrumbs() 补上操作序列，这是本模块 P0-19 的部分。
 */

import apmClient from '@apmplus/web';
import { isAPMEnabled } from '@/config/apm';
import { getCurrentFeature, getCurrentModelName } from './context';
import { recentFrames } from './window';

const sessionStartedAt = Date.now();

interface MemoryInfo {
  usedJSHeapSize: number;
  jsHeapSizeLimit: number;
}

const getMemRatio = (): number => {
  const mem = (performance as Performance & { memory?: MemoryInfo }).memory;
  return mem && mem.jsHeapSizeLimit > 0 ? mem.usedJSHeapSize / mem.jsHeapSizeLimit : 0;
};

/** 低基数字段，可直接作为 extra/categories 使用 */
export function errorSnapshot(): Record<string, string> {
  return {
    feature: getCurrentFeature(),
    modelName: getCurrentModelName(),
    netType: (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType ?? 'unknown',
    memPressure: getMemRatio() > 0.8 ? 'high' : 'normal',
    sessionAgeMin: String(Math.floor((Date.now() - sessionStartedAt) / 60_000)),
  };
}

/**
 * 高基数上下文（触发原因、完整状态、面包屑）另发一条日志承载。
 * @param trigger 触发场景标识，如 'canvas_save_failed' / 'ai_stream_abnormal'
 * @param detail 需要另外记录的高基数字段（如 canvasId / chatId）
 */
export function dumpErrorDetail(trigger: string, detail: Record<string, unknown> = {}): void {
  // 本模块直连 SDK（不经 apm.ts 的封装），需自行守闸门，否则非 prod 会进 SDK 预收集队列
  if (!isAPMEnabled()) return;
  const snapshot = errorSnapshot();
  try {
    apmClient.sendLog?.({
      level: 'error',
      content: JSON.stringify({
        trigger,
        ...detail,
        url: location.href,
        breadcrumbs: apmClient.getBreadcrumbs?.()?.slice(-20) ?? [],
        // 业务语义现场（画布保存/加载阶段变化等）：与 breadcrumbs 互补，见 window.ts 头注释
        windowFrames: recentFrames(),
      }).slice(0, 1024),
      extra: { trigger, ...snapshot },
    });
  } catch {
    // 快照上报失败不应影响主流程
  }
}
